import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * Shared layout for the four member-facing workspace areas.
 *
 * These pages are deliberately styled as a calm professional workspace —
 * not a course or classroom. We avoid words like "lesson", "module",
 * "complete", "progress" etc.
 */
export function WorkspaceShell({
  testId,
  eyebrow,
  title,
  intro,
  memberBadge,
  accent = "teal", // "teal" or "purple"
  children,
}) {
  const { user } = useAuth();
  const accentText = accent === "purple" ? "text-purple" : "text-teal";
  const accentBg = accent === "purple" ? "bg-purple" : "bg-teal";
  const accentSurface = accent === "purple" ? "bg-purple/5" : "bg-teal/5";
  const accentBorder = accent === "purple" ? "border-purple/15" : "border-teal/15";

  return (
    <div data-testid={testId} className="bg-[#fafaf7] min-h-[80vh]">
      {/* Workspace header strip */}
      <div className={`border-b border-slate-200 ${accentSurface}`}>
        <div className="container-jcc py-5 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <span
              aria-hidden="true"
              className={`w-2.5 h-2.5 rounded-full ${accentBg}`}
            />
            <div className="text-sm text-ink-muted truncate">
              <Link to="/" className="hover:text-ink transition-colors">
                JCC
              </Link>
              <span className="px-2 text-ink-soft">/</span>
              <span className="text-ink font-medium">Member workspace</span>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {memberBadge && (
              <span
                className={`pill border ${accentBorder} ${accentText} bg-white`}
                data-testid="workspace-member-badge"
              >
                {memberBadge}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Welcome / hero */}
      <header className="border-b border-slate-200 bg-white">
        <div className="container-jcc py-12 md:py-16">
          {eyebrow && (
            <div className={`overline ${accentText} mb-3`} data-testid="workspace-eyebrow">
              {eyebrow}
            </div>
          )}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1] max-w-3xl">
            {title}
          </h1>
          {user?.name && (
            <p className="mt-3 text-ink-muted">
              Welcome back, {user.name.split(" ")[0]}.
            </p>
          )}
          {intro && (
            <p className="mt-4 text-base md:text-lg text-ink-muted leading-relaxed max-w-3xl">
              {intro}
            </p>
          )}
        </div>
      </header>

      {/* Workspace body */}
      <div className="container-jcc py-10 md:py-14 space-y-12">{children}</div>
    </div>
  );
}

export function WorkspaceSection({
  id,
  title,
  description,
  children,
  action,
  testId,
}) {
  return (
    <section id={id} data-testid={testId} className="scroll-mt-24">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl md:text-[1.75rem] font-semibold tracking-tight">
            {title}
          </h2>
          {description && (
            <p className="mt-1.5 text-ink-muted text-sm md:text-base leading-relaxed">
              {description}
            </p>
          )}
        </div>
        {action}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{children}</div>
    </section>
  );
}

/**
 * Single workspace card.
 *
 * status (optional): "coming-soon" | "available" | "external"
 * href (optional): if provided, the card is clickable. Use `external` to render <a>.
 */
export function WorkspaceCard({
  title,
  body,
  icon: Icon,
  href,
  external = false,
  status = "coming-soon",
  ctaLabel,
  accent = "teal",
  testId,
}) {
  const accentText = accent === "purple" ? "text-purple" : "text-teal";
  const accentIconBg =
    accent === "purple" ? "bg-purple/10" : "bg-teal/10";

  const statusMeta = {
    "coming-soon": {
      label: "Content coming soon",
      classes: "bg-slate-100 text-ink-muted",
    },
    available: {
      label: "Open",
      classes:
        accent === "purple"
          ? "bg-purple/10 text-purple"
          : "bg-teal/10 text-teal",
    },
    external: {
      label: "Opens in Systeme",
      classes: "bg-amber-50 text-amber-800 border border-amber-200",
    },
  }[status] || { label: "", classes: "" };

  const inner = (
    <div className="h-full flex flex-col card-base p-6 group">
      <div className="flex items-start justify-between gap-3 mb-4">
        {Icon ? (
          <div
            className={`w-11 h-11 rounded-xl ${accentIconBg} ${accentText} flex items-center justify-center`}
            aria-hidden="true"
          >
            <Icon className="w-5 h-5" strokeWidth={1.75} />
          </div>
        ) : (
          <span aria-hidden="true" />
        )}
        {statusMeta.label && (
          <span className={`pill ${statusMeta.classes}`}>
            {statusMeta.label}
          </span>
        )}
      </div>
      <h3 className="font-display text-lg font-semibold leading-snug">
        {title}
      </h3>
      {body && (
        <p className="mt-2 text-sm text-ink-muted leading-relaxed">{body}</p>
      )}
      {(href || ctaLabel) && (
        <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-sm font-medium">
          <span className={accentText}>{ctaLabel || "Open"}</span>
          {external ? (
            <ArrowUpRight
              className={`w-4 h-4 ${accentText} group-hover:translate-x-0.5 transition-transform`}
              strokeWidth={2}
            />
          ) : (
            <ChevronRight
              className={`w-4 h-4 ${accentText} group-hover:translate-x-0.5 transition-transform`}
              strokeWidth={2}
            />
          )}
        </div>
      )}
    </div>
  );

  if (href) {
    if (external) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="block focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2 rounded-2xl"
          data-testid={testId}
        >
          {inner}
        </a>
      );
    }
    return (
      <Link
        to={href}
        className="block focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2 rounded-2xl"
        data-testid={testId}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={testId} className="cursor-default">
      {inner}
    </div>
  );
}

/**
 * Prominent "Start here" panel. One per workspace.
 */
export function StartHereCard({
  title,
  body,
  accent = "teal",
  href,
  ctaLabel = "Begin here",
  external = false,
  testId = "workspace-start-here",
}) {
  const accentBg = accent === "purple" ? "bg-purple" : "bg-teal";
  const accentText = accent === "purple" ? "text-purple" : "text-teal";

  const cta =
    href && external ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`btn-${accent === "purple" ? "purple" : "teal"} mt-5`}
      >
        {ctaLabel} <ArrowUpRight className="w-4 h-4" />
      </a>
    ) : href ? (
      <Link
        to={href}
        className={`btn-${accent === "purple" ? "purple" : "teal"} mt-5`}
      >
        {ctaLabel} <ChevronRight className="w-4 h-4" />
      </Link>
    ) : (
      <span className="mt-5 inline-flex items-center pill bg-slate-100 text-ink-muted">
        Content coming soon
      </span>
    );

  return (
    <section
      data-testid={testId}
      className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-7 md:p-10"
    >
      <div className={`absolute left-0 top-0 h-full w-1.5 ${accentBg}`} aria-hidden="true" />
      <div className={`overline ${accentText} mb-3`}>Start here</div>
      <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight max-w-2xl">
        {title}
      </h2>
      {body && (
        <p className="mt-3 text-ink-muted leading-relaxed max-w-2xl">{body}</p>
      )}
      {cta}
    </section>
  );
}

/**
 * Support / next steps footer block.
 */
export function WorkspaceSupportBlock({
  testId = "workspace-support",
}) {
  return (
    <section
      data-testid={testId}
      className="rounded-3xl bg-white border border-slate-200 p-7 md:p-9"
    >
      <div className="grid md:grid-cols-2 gap-6 md:gap-10">
        <div>
          <h3 className="font-display text-lg font-semibold">Need a hand?</h3>
          <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">
            We&apos;re a small team building this carefully. Reach out and we&apos;ll
            help where we can.
          </p>
          <Link to="/contact" className="btn-outline !py-2 !px-4 text-sm mt-4">
            Contact JCC
          </Link>
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold">FAQs</h3>
          <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">
            Common questions from carers and families using JCC.
          </p>
          <Link to="/faq" className="btn-outline !py-2 !px-4 text-sm mt-4">
            Read the FAQs
          </Link>
        </div>
      </div>
      <p className="mt-7 text-xs text-ink-soft leading-relaxed border-t border-slate-100 pt-5">
        <strong>JCC is not a care agency.</strong> JCC provides guidance,
        templates and a private workspace. It does not employ, supply, vet,
        manage, supervise or guarantee carers. All arrangements are between
        the self-employed carer and the family or client representative.
      </p>
    </section>
  );
}
